import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit, Trash2, LogOut, Eye, EyeOff, Upload, User } from 'lucide-react';
import { useAdmin } from '../../contexts/AdminContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const { logout, getAuthHeaders, isAuthenticated } = useAdmin();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/admin');
      return;
    }
    fetchCampaigns();
    fetchProfilePhoto();
  }, [isAuthenticated, navigate]);

  const fetchCampaigns = async () => {
    try {
      // Send admin auth so we get draft campaigns too
      const response = await fetch(`${API_URL}/api/campaigns/`, {
        headers: getAuthHeaders()
      });
      const data = await response.json();
      setCampaigns(data);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProfilePhoto = async () => {
    try {
      const res = await fetch(`${API_URL}/api/profile/`);
      const data = await res.json();
      setProfilePhoto(data.profilePhoto);
    } catch (e) {
      console.error('Error fetching profile photo:', e);
    }
  };

  const resolvePhotoUrl = (url) => {
    if (!url) return '';
    const clean = url.split('?')[0];
    const suffix = url.includes('?') ? url.substring(url.indexOf('?')) : '';
    return (clean.startsWith('/api/') ? `${API_URL}${clean}` : clean) + suffix;
  };

  const handleUploadProfilePhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      alert('Please choose an image file (JPG, PNG or WebP).');
      return;
    }
    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${API_URL}/api/profile/upload-photo`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: formData,
      });
      if (!res.ok) throw new Error('Upload failed');
      const data = await res.json();
      setProfilePhoto(`${data.profilePhoto}?v=${Date.now()}`);
    } catch (err) {
      console.error(err);
      alert('Failed to upload profile photo. Please try again.');
    } finally {
      setUploadingPhoto(false);
      e.target.value = '';
    }
  };

  const handleTogglePublish = async (campaignId) => {
    try {
      const response = await fetch(
        `${API_URL}/api/campaigns/${campaignId}/toggle-publish`,
        { method: 'POST', headers: getAuthHeaders() }
      );
      if (response.ok) {
        await fetchCampaigns();
      } else {
        alert('Failed to toggle publish state');
      }
    } catch (error) {
      console.error('Error toggling publish:', error);
      alert('Error toggling publish state');
    }
  };

  const handleDelete = async (campaignId) => {
    try {
      const response = await fetch(`${API_URL}/api/campaigns/${campaignId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        setCampaigns(campaigns.filter(c => c.id !== campaignId));
        setDeleteConfirm(null);
      } else {
        alert('Failed to delete campaign');
      }
    } catch (error) {
      console.error('Error deleting campaign:', error);
      alert('Error deleting campaign');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/admin');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Campaign Dashboard</h1>
            <p className="text-sm text-slate-600">Manage your portfolio campaigns</p>
          </div>
          <div className="flex items-center gap-4">
            <a 
              href="/" 
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 text-slate-700 hover:text-teal-600 transition-colors"
            >
              <Eye className="w-4 h-4" />
              View Site
            </a>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Profile Photo Card */}
        <div className="mb-8 bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
            <div className="w-24 h-24 rounded-2xl overflow-hidden border-4 border-teal-100 shadow-md bg-slate-100 flex-shrink-0 flex items-center justify-center">
              {profilePhoto ? (
                <img
                  src={resolvePhotoUrl(profilePhoto)}
                  alt="Current profile"
                  className="w-full h-full object-cover"
                  data-testid="admin-current-profile-photo"
                />
              ) : (
                <User className="w-10 h-10 text-slate-400" />
              )}
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-bold text-slate-900">Profile Photo</h2>
              <p className="text-sm text-slate-600 mb-3">
                Shown on your homepage hero and used as your Open Graph avatar. Recommended: square, 800×800px, JPG or PNG.
              </p>
              <label
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium cursor-pointer transition-colors ${
                  uploadingPhoto
                    ? 'bg-slate-200 text-slate-500 cursor-not-allowed'
                    : 'bg-teal-600 text-white hover:bg-teal-700'
                }`}
              >
                <Upload className="w-4 h-4" />
                {uploadingPhoto ? 'Uploading…' : 'Upload New Photo'}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleUploadProfilePhoto}
                  disabled={uploadingPhoto}
                  data-testid="admin-profile-photo-input"
                  className="hidden"
                />
              </label>
            </div>
          </div>
        </div>

        {/* Add Campaign Button */}
        <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
          <button
            onClick={() => navigate('/admin/campaign/new')}
            data-testid="admin-add-campaign-btn"
            className="flex items-center gap-2 px-6 py-3 bg-teal-600 text-white rounded-lg font-semibold hover:bg-teal-700 transition-colors shadow-lg hover:shadow-xl"
          >
            <Plus className="w-5 h-5" />
            Add New Campaign
          </button>
          <p className="text-sm text-slate-500">
            Campaigns are sorted automatically: newest year first, and the most recently added campaign at the top of its year.
          </p>
        </div>

        {/* Campaigns List */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">Campaign</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">Brand</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">Category</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">Year</th>
                  <th className="px-6 py-4 text-right text-sm font-semibold text-slate-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {campaigns.map((campaign) => (
                  <tr key={campaign.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-4">
                        <img 
                          src={campaign.thumbnail} 
                          alt={campaign.title}
                          className="w-16 h-16 object-cover rounded-lg"
                        />
                        <div>
                          <p className="font-semibold text-slate-900">{campaign.title}</p>
                          <p className="text-sm text-slate-600">{campaign.shortDescription.substring(0, 60)}...</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-slate-700">{campaign.brand}</td>
                    <td className="px-6 py-4">
                      <span className="px-3 py-1 rounded-full text-sm font-medium bg-teal-100 text-teal-700">
                        {campaign.category}
                      </span>
                      {campaign.published === false && (
                        <span
                          data-testid={`admin-campaign-draft-badge-${campaign.id}`}
                          className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-200"
                          title="This campaign is a draft — not visible on the public site"
                        >
                          Draft
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-slate-700">{campaign.year}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleTogglePublish(campaign.id)}
                          data-testid={`admin-campaign-toggle-publish-${campaign.id}`}
                          className={`p-2 rounded-lg transition-colors ${campaign.published !== false ? 'text-teal-600 hover:bg-teal-50' : 'text-amber-600 hover:bg-amber-50'}`}
                          title={campaign.published !== false ? 'Published — click to unpublish (make draft)' : 'Draft — click to publish'}
                        >
                          {campaign.published !== false ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                        </button>
                        <button
                          onClick={() => navigate(`/admin/campaign/${campaign.id}`)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Edit"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(campaign.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {campaigns.length === 0 && (
          <div className="text-center py-12 bg-white rounded-xl border border-slate-200">
            <p className="text-slate-600 mb-4">No campaigns yet</p>
            <button
              onClick={() => navigate('/admin/campaign/new')}
              className="text-teal-600 hover:text-teal-700 font-semibold"
            >
              Add your first campaign
            </button>
          </div>
        )}
      </main>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-6">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <h3 className="text-xl font-bold text-slate-900 mb-2">Delete Campaign?</h3>
            <p className="text-slate-600 mb-6">
              Are you sure you want to delete this campaign? This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
